import { NovelItem } from '@app/_proto/Protos/novels';
import { GetSectionRequest, SectionType } from '@app/_proto/Protos/sections';
import { SectionsClient } from '@app/_proto/Protos/sections.client';
import { VipItem_VipType } from '@app/_proto/Protos/vips';
import { Any } from '@app/_proto/google/protobuf/any';
import useUnlocks from '@app/domains/common/hooks/useUnlocks';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import HomeSubscriptionsContent from '@app/domains/home/components/HomeSubscriptionsContent';
import HomeSwiper from '@app/domains/home/components/HomeSwiper';
import useHomeMediaQuery from '@app/domains/home/hooks/useHomeMediaQuery';
import { HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE } from '@app/domains/home/styles';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { getOneofValue, useGrpcRequest } from '@app/libs/grpc';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

export default function HomeNovelsSubscriptions() {
    const [ref, inView] = useInView({
        triggerOnce: true,
    });

    const { isLargeScreen } = useHomeMediaQuery();

    const { user } = useAuth();

    const sectionRequest = useGrpcRequest(GetSectionRequest, {
        type: SectionType.NovelCTA,
    });

    const { data: unlockedNovels, isLoading: isUnlockedNovelsLoading } = useUnlocks({
        user: user ?? undefined,
        staleTime: 1000 * 60 * 60 * 24,
        enabled: !!user && inView,
    });

    const { data, isLoading, isSuccess } = useGrpcApiWithQuery(
        SectionsClient,
        c => c.getSection,
        sectionRequest,
        ['sections', SectionType.NovelCTA, user?.id],
        {
            enabled: !!user && inView,
            keepPreviousData: false,
            cacheTime: 1000 * 60 * 60 * 24, //Data cached for 24 hours
            staleTime: 1000 * 60 * 60 * 24, //Data stales in 24 hours
            refetchOnWindowFocus: false,
            refetchOnMount: true,
        }
    );

    const novels = useMemo(() => {
        const unpackedNovels = uniqBy(
            data?.item?.contents.map(content => {
                return Any.unpack(content, NovelItem)!;
            }),
            n => n.id
        );

        if (!unlockedNovels || !user?.isVipActive) {
            return unpackedNovels;
        }

        //For VIP Diamond user, don't show any novel that can be unlocked with VIP.
        if (user.vip?.type === VipItem_VipType.Diamond) {
            return unpackedNovels?.filter(n => !n.karmaInfo?.canUnlockWithVip);
        }

        //If a VIP user has already unlocked free novel and that novel is in the subscription list
        //don't show that novel.
        return unpackedNovels?.filter(n => !unlockedNovels.some(un => getOneofValue(un.id, 'novelId') === n.id));
    }, [data?.item, unlockedNovels, user]);

    const slidesPerView = isLargeScreen ? 2 : 1;

    const dataIsLoading = !novels && isUnlockedNovelsLoading;

    return (
        <div ref={ref} className="my-40">
            <div
                data-posthog-params={JSON.stringify({
                    collectionSlot: HomeCollectionSlots.Subscriptions,
                })}
            >
                <HomeSwiper
                    slidesPerView={'auto'}
                    slidesPerGroup={slidesPerView}
                    className={HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE}
                    isLoading={dataIsLoading}
                >
                    {(novels ?? Array(2).fill(null))?.map((novel, idx) => (
                        <HomeSwiper.Slide
                            key={novel?.id ?? `HomeNovelsSubscriptions-${idx}`}
                            className="!w-[calc((100%+12px)-12px)] sm:!w-[calc((532px+12px)-12px)] lg:!w-[calc((100%+16px)/2-16px)]"
                            data-posthog-params={JSON.stringify({ itemSlot: idx })}
                        >
                            <HomeSubscriptionsContent isLoading={dataIsLoading} novel={novel} />
                        </HomeSwiper.Slide>
                    ))}
                </HomeSwiper>
            </div>
        </div>
    );
}
